import { certificationService } from '@/services/certification.service';
import { reviewService } from '@/services/review.service';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  ChevronDown,
  Clock,
  Edit,
  Grid,
  List,
  PenLine,
  Search,
  SlidersHorizontal,
  Sparkle,
  Star,
  Target,
  TrendingUp
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ADMIN_CERTIFICATION_CREATE_MANUAL } from '@/constants/constant-routes';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_CATALOG_PERMISSION, CERTIFICATION_CREATION_PERMISSION } from '@/constants/constant-permissions';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import TooltipArrow from '@/components/ui/TooltipArrow';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { useDebounce } from '@/utils/debounce';

interface Certification {
  _id: string;
  certification_id: string;
  title: string;
  description: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  is_published: boolean;
  time_limit_minutes: number;
  passing_score: number;
  total_points: number;
  question_count: number;
  version: number;
  settings: {
    shuffle_questions: boolean;
    shuffle_options: boolean;
    show_feedback: boolean;
    show_correct_answers: boolean;
    allow_retry: boolean;
    max_attempts: number;
  };
  rating?: number;
  review_count?: number;
  createdAt: Date;
  updatedAt: Date;
}

const CertificationCard = ({ certification, isListView = false, onRatingClick, canWrite = true }: { certification: Certification, isListView?: boolean, onRatingClick?: (id: string) => void, canWrite?: boolean }) => {
  const getStatusColor = (isPublished: boolean) => {
    return isPublished
      ? 'bg-green-100 text-green-700 border-green-300'
      : 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Get thumbnail URL
  const thumbnailUrl = certification.thumbnail_image_url || certification.banner_image_url || 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400';

  // Format time limit
  const formatTimeLimit = () => {
    const minutes = certification.time_limit_minutes;
    if (minutes === 0) return 'No limit';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  return (
    <div className={`card card-hover ${isListView ? 'flex flex-col sm:flex-row gap-3 sm:gap-4' : 'flex flex-col'}`}>
      <div className={`relative ${isListView ? 'w-full sm:w-48 flex-shrink-0' : ''}`}>
        <img
          src={thumbnailUrl}
          alt={certification.title}
          className={`w-full ${isListView ? 'h-40 sm:h-32' : 'h-40 sm:h-48'} object-cover rounded-lg`}
        />
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(certification.is_published)} backdrop-blur-sm`}>
            {certification.is_published ? '🟢 Published' : '⚫ Draft'}
          </span>
        </div>
        {/* <div className="absolute bottom-2 left-2">
          <span className="px-1.5 sm:px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
            v{certification.version}
          </span>
        </div> */}
      </div>

      <div className={`${isListView ? 'flex-1' : 'mt-3 sm:mt-4'} flex flex-col flex-1 min-w-0`}>
        <h3 className={`font-semibold text-gray-900 ${isListView ? 'text-base sm:text-lg' : 'text-sm sm:text-base'} line-clamp-2 mb-2`}>
          <Link
            to={canWrite ? `/admin/certifications/${certification.certification_id}` : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            title={!canWrite ? 'You do not have permission to perform this action' : undefined}
            className={`hover:text-primary-600 ${!canWrite ? 'cursor-not-allowed opacity-70 pointer-events-auto' : ''}`}
          >
            {certification.title}
          </Link>
        </h3>
        <TooltipArrow content={certification.description} position="bottom" block>
          <p className={`text-gray-600 text-xs sm:text-sm mb-3 break-words ${isListView ? 'line-clamp-2' : 'line-clamp-3'}`}>
            {certification.description}
          </p>
        </TooltipArrow>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
          {/* Rating Display - Always show */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (canWrite && onRatingClick) {
                onRatingClick(certification.certification_id);
              }
            }}
            disabled={!canWrite}
            className={`flex items-center gap-1 hover:bg-yellow-50 px-1.5 py-0.5 rounded transition-colors ${!canWrite ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            title={!canWrite ? 'You do not have permission to perform this action' : 'View reviews'}
          >
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{certification.rating?.toFixed(1) || '0.0'}</span>
            <span className="text-gray-400">• {certification.review_count || 0} ratings</span>
          </button>
          <div className="flex items-center gap-1" title="Time Limit">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{formatTimeLimit()}</span>
          </div>
          <div className="flex items-center gap-1" title="Passing Score">
            <Target className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{certification.passing_score}%</span>
          </div>
          <div className="flex items-center gap-1" title="Total Points">
            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{certification.total_points} pts</span>
          </div>
          <div className="flex items-center gap-1" title="Questions">
            <Award className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{certification.question_count} Q</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mt-auto">
          <div className="text-xs text-gray-500">
            {certification.settings.max_attempts ? `Max ${certification.settings.max_attempts} attempts` : 'Unlimited attempts'}
          </div>
          <Link
            to={canWrite ? `/admin/certifications/${certification.certification_id}` : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            title={!canWrite ? 'You do not have permission to perform this action' : undefined}
            className={`btn btn-primary btn-sm inline-flex items-center justify-center gap-2 w-full sm:w-auto ${!canWrite ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Edit size={14} />
            Manage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function Certifications() {
  usePermissionRouteAccess([
    CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_READ,
    CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_WRITE,
  ]);
  const canWrite = useHasPermission([CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_WRITE]);
  const canCreate = useHasPermission([CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_WRITE]);

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(21);
  const [filters, setFilters] = useState({
    search: '',
    is_published: '' as '' | 'true' | 'false',
    sortBy: 'updatedAt' as 'title' | 'time_limit' | 'passing_score' | 'total_points' | 'question_count' | 'createdAt' | 'updatedAt',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  // Debounce search input
  const debouncedSearch = useDebounce(filters.search, 1000);

  // Handle rating click - navigate to preview with reviews tab
  const handleRatingClick = (certificationId: string) => {
    navigate(`/admin/certification/preview/${certificationId}?tab=reviews`);
  };

  const queryParams = useMemo(() => {
    const params: any = {
      page,
      limit: itemsPerPage,
      search: debouncedSearch,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    };

    // Add is_published filter
    if (filters.is_published !== '') {
      params.is_published = filters.is_published === 'true';
    }

    // Remove empty values
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    return params;
  }, [filters, debouncedSearch, page, itemsPerPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['minimal-certifications', queryParams],
    queryFn: () => certificationService.getMinimalCertifications(queryParams),
    placeholderData: (prev) => prev,
  });

  if (error) toast.error('Failed to fetch certifications.');

  const rawCertifications = data?.certifications || [];
  const pagination = data?.pagination;
  const totalCertifications = data?.total || 0;
  const publishedCount = data?.published_count || 0;
  const unpublishedCount = data?.unpublished_count || 0;
  const totalPages = pagination?.totalPages || 1;

  // Fetch review stats for all certifications
  const { data: reviewStatsMap } = useQuery({
    queryKey: ['certification-review-stats-bulk'],
    queryFn: () => reviewService.getBatchCertificationStats(),
  });

  // Enrich certifications with review stats
  const certifications = rawCertifications.map((certification: Certification) => {
    const stats = reviewStatsMap?.get(certification.certification_id);
    return {
      ...certification,
      rating: stats?.avg_rating ?? certification.rating ?? 0,
      review_count: stats?.total_reviews ?? certification.review_count ?? 0,
    };
  });

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Draft and Published are the only two states — an unpublished certification is
  // a draft, and publishing is one-way.
  const statusOptions: SelectOption[] = [
    { value: '', label: '⚪ All' },
    { value: 'true', label: '🟢 Published' },
    { value: 'false', label: '⚫ Draft' },
  ];

  // SimpleSelect reports the '' ("All") option as null, so coerce it back.
  const handleStatusChange = (value: string | number | null) => {
    setPage(1);
    setFilters(prev => ({ ...prev, is_published: (value == null ? '' : String(value)) as '' | 'true' | 'false' }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const sortOptions = [
    { value: 'updatedAt-desc', label: 'Recently Updated' },
    { value: 'createdAt-desc', label: 'Newest' },
    { value: 'title-asc', label: 'Title (A-Z)' },
    { value: 'title-desc', label: 'Title (Z-A)' },
    { value: 'time_limit-asc', label: 'Time Limit (Low to High)' },
    { value: 'time_limit-desc', label: 'Time Limit (High to Low)' },
    { value: 'passing_score-desc', label: 'Passing Score (High to Low)' },
    { value: 'passing_score-asc', label: 'Passing Score (Low to High)' },
    { value: 'total_points-desc', label: 'Total Points (High to Low)' },
    { value: 'question_count-desc', label: 'Most Questions' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-brand-yellow-500 to-brand-orange-500 rounded-xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <SkeletonLoader type="custom" width="40px" height="40px" className="rounded-lg bg-white/20" />
              <div className="flex-1">
                <SkeletonLoader type="title" className="h-6 sm:h-8 w-40 sm:w-48 mb-2 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 sm:h-4 w-64 sm:w-96 bg-white/20 hidden sm:block" />
              </div>
            </div>
            <SkeletonLoader type="custom" width="180px" height="40px" className="rounded-lg bg-white/20" />
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <SkeletonLoader type="text" className="h-5 sm:h-7 w-12 sm:w-16 mb-1 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 w-16 sm:w-20 bg-white/20" />
              </div>
            ))}
          </div>
        </div>

        {/* Search and Controls Skeleton */}
        <div className="card p-4 md:p-6">
          <div className="flex flex-col gap-4 mb-6">
            <SkeletonLoader type="custom" height="44px" className="rounded-lg w-full" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <SkeletonLoader type="custom" width="140px" height="40px" className="rounded-lg" />
              <div className="flex items-center gap-2 sm:gap-3">
                <SkeletonLoader type="custom" width="80px" height="40px" className="rounded-lg" />
                <SkeletonLoader type="custom" width="180px" height="40px" className="rounded-lg flex-1 sm:flex-initial" />
              </div>
            </div>
          </div>
        </div>

        {/* Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {Array.from({ length: 9 }).map((_, index) => (
            <div key={index} className="card flex flex-col">
              <SkeletonLoader type="custom" height="160px" className="rounded-lg mb-3 sm:mb-4" />
              <SkeletonLoader type="title" className="mb-2" />
              <SkeletonLoader type="text" className="mb-1" />
              <SkeletonLoader type="text" className="mb-3" />
              <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 sm:mb-4">
                <SkeletonLoader type="text" className="w-16" />
                <SkeletonLoader type="text" className="w-16" />
                <SkeletonLoader type="text" className="w-16" />
                <SkeletonLoader type="text" className="w-16" />
              </div>
              <div className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <SkeletonLoader type="text" className="w-24" />
                <SkeletonLoader type="custom" width="100px" height="32px" className="rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-gradient-to-r from-brand-yellow-500 to-brand-orange-500 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <Award className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Certification Catalog</h1>
              <p className="text-sm sm:text-base text-brand-yellow-100 hidden sm:block">Discover and manage all certification in the platform.
              </p>
            </div>  
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to={canCreate ? ADMIN_CERTIFICATION_CREATE_MANUAL : "#"}
              onClick={(e) => !canCreate && e.preventDefault()}
              title={!canCreate ? 'You do not have permission to perform this action' : 'Create a certification and write the questions yourself'}
              className={`flex items-center justify-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg transition-colors whitespace-nowrap ${canCreate ? 'hover:bg-white/30' : 'opacity-50 cursor-not-allowed'}`}
            >
              <PenLine className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">Create Manually</span>
            </Link>
            <Link
              to={canCreate ? "/admin/certification/create" : "#"}
              onClick={(e) => !canCreate && e.preventDefault()}
              title={!canCreate ? 'You do not have permission to perform this action' : undefined}
              className={`flex items-center justify-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg transition-colors whitespace-nowrap ${canCreate ? 'hover:bg-white/30' : 'opacity-50 cursor-not-allowed'}`}
            >
              <Sparkle className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">Create Certification</span>
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{totalCertifications}</div>
            <div className="text-xs sm:text-sm text-brand-yellow-200">Total</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{publishedCount}</div>
            <div className="text-xs sm:text-sm text-brand-yellow-200">Published</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{unpublishedCount}</div>
            <div className="text-xs sm:text-sm text-brand-yellow-200">Draft</div>
          </div>
        </div>
      </div>

      <div className="card p-4 md:p-6">
        <div className="flex flex-col gap-4 mb-6">
          {/* Search bar - full width on mobile */}
          <div className="w-full">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search certifications..."
                className="w-full pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm md:text-base"
              />
            </div>
          </div>

          {/* Controls row - stacked on mobile, side by side on tablet+ */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm md:text-base"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-600'}`}
                  aria-label="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-600'}`}
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              {/* Keeps the original flex-1 / fixed-width toolbar behaviour, since
                  SimpleSelect itself is always w-full. */}
              <div className="flex-1 sm:flex-initial sm:w-52">
                <SimpleSelect
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={(value) => {
                    if (value == null) return;
                    const [sortBy, sortOrder] = String(value).split('-') as [typeof filters.sortBy, typeof filters.sortOrder];
                    setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                  }}
                  options={sortOptions}
                  isSearchable={false}
                />
              </div>
            </div>
          </div>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg mb-4 sm:mb-6">
            <SimpleSelect
              label="Status"
              value={filters.is_published}
              onChange={handleStatusChange}
              options={statusOptions}
              isSearchable={false}
            />
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({
                    search: '',
                    is_published: '',
                    sortBy: 'updatedAt',
                    sortOrder: 'desc'
                  });
                  setPage(1);
                }}
                className="w-full btn btn-secondary text-sm md:text-base"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {certifications.length === 0 ? (
        <div className="text-center py-12 card">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No certifications found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your filters or create a new certification.</p>
          <Link
            to={canCreate ? "/admin/certification/create" : "#"}
            onClick={(e) => !canCreate && e.preventDefault()}
            title={!canCreate ? 'You do not have permission to perform this action' : undefined}
            className={`btn btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Create Certification
          </Link>
        </div>
      ) : (
        <>
          <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}`}>
            {certifications.map((certification: Certification) => (
              <CertificationCard key={certification._id} certification={certification} isListView={viewMode === 'list'} onRatingClick={handleRatingClick} canWrite={canWrite} />
            ))}
          </div>

          {/* Pagination - Always show when there's data */}
          {totalCertifications > 0 && (
            <div className="mt-6">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalCertifications}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[9, 18, 21, 30]}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
